export type MetricUnit = 'currency' | 'count' | 'ratio' | 'percent';

const compact = new Intl.NumberFormat('en-US', { notation: 'compact', maximumFractionDigits: 1 });
const integer = new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 });
const decimals2 = new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const percent1 = new Intl.NumberFormat('en-US', { style: 'percent', minimumFractionDigits: 1, maximumFractionDigits: 1 });
const ratio2 = new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export function formatMetric(value: number, unit: MetricUnit, opts: { compact?: boolean } = {}): string {
  if (!Number.isFinite(value)) return '—';
  if (unit === 'currency') {
    return opts.compact ? `${compact.format(value)} PLN` : `${decimals2.format(value)} PLN`;
  }
  if (unit === 'percent') {
    return percent1.format(value);
  }
  if (unit === 'ratio') {
    return `${ratio2.format(value)}×`;
  }
  return opts.compact ? compact.format(value) : integer.format(value);
}

// OKR percent targets are entered in percent points (e.g. 3.7) but stored as
// fractions (0.037) to match how percent actuals are stored and formatted.
// These two helpers convert across that boundary in the target editors.
export function targetToField(targetValue: number, unit: string): string {
  if (unit !== 'percent' || !Number.isFinite(targetValue)) return String(targetValue);
  // toFixed avoids 0.037 * 100 => 3.6999999999999997 leaking into the field.
  return String(Number((targetValue * 100).toFixed(6)));
}

export function fieldToTarget(field: string, unit: string): number {
  const n = Number(field);
  if (unit !== 'percent' || !Number.isFinite(n)) return n;
  return Number((n / 100).toFixed(8));
}

export function formatDelta(deltaPct: number | null): { label: string; positive: boolean | null } {
  if (deltaPct === null || !Number.isFinite(deltaPct)) return { label: '—', positive: null };
  const sign = deltaPct > 0 ? '+' : '';
  return {
    label: `${sign}${percent1.format(deltaPct)}`,
    positive: deltaPct === 0 ? null : deltaPct > 0,
  };
}

export function formatDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// Sheet month keys are "YYYY-MM" (e.g. "2025-08" → "Aug 25").
export function formatMonthLabel(key: string): string {
  const m = /^(\d{4})-(\d{2})$/.exec(key);
  if (!m) return key;
  const monthIdx = parseInt(m[2], 10) - 1;
  if (monthIdx < 0 || monthIdx > 11) return key;
  return `${MONTHS_SHORT[monthIdx]} ${m[1].slice(2)}`;
}

// "Jul 26" for one month, "Jul – Sep 26" for a run of them (the months that
// actually fed a key result's quarter figure).
export function formatMonthRange(months: string[]): string {
  if (months.length === 0) return '';
  const first = formatMonthLabel(months[0]);
  if (months.length === 1) return first;
  const last = formatMonthLabel(months[months.length - 1]);
  const sameYear = months[0].slice(0, 4) === months[months.length - 1].slice(0, 4);
  return `${sameYear ? first.slice(0, 3) : first} – ${last}`;
}

// Maps the sheet's number format to a MetricUnit. The API already derives `unit`,
// so this is a fallback for places that only have the raw `format` string.
export function sheetFormatToUnit(format: string): MetricUnit {
  switch (format) {
    case 'currency':
      return 'currency';
    case 'percentage':
      return 'percent';
    case 'decimal':
      return 'ratio';
    default:
      return 'count';
  }
}

export function formatDateTime(iso: string | Date): string {
  const d = typeof iso === 'string' ? new Date(iso) : iso;
  return d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

export function formatDuration(ms: number): string {
  if (ms < 1000) return `${ms} ms`;
  const seconds = ms / 1000;
  if (seconds < 60) return `${seconds.toFixed(1)} s`;
  const minutes = Math.floor(seconds / 60);
  const rem = Math.round(seconds % 60);
  return `${minutes}m ${rem}s`;
}
